import React, { useState, useEffect, useCallback } from 'react';
import { Home } from 'lucide-react';
import sendCertificationEmail from './certificateEmailService';
import { validateFormCompletion, markFormCompleted, lookupVesselBySerial, assertVesselNotScrapped, isVesselScrapped, getScrappedFormMessage } from './formTraceabilityService';
import { getCurrentUserDisplayName } from '../utils/currentUser';
import SerialValidationModal from './SerialValidationModal';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';

const MonthYearPicker = ({ value, onChange, label, required }) => {
 const months = [
   'January', 'February', 'March', 'April', 'May', 'June',
   'July', 'August', 'September', 'October', 'November', 'December'
 ];

 const currentYear = new Date().getFullYear();
 const years = Array.from({length: 32}, (_, i) => currentYear + 1 - 31 + i);
 const [selectedMonth, selectedYear] = value ? value.split('-') : ['', ''];
 
 return (
   <div>
     <label className="block text-sm font-medium text-pvs-text-muted mb-2">
       {label} {required && '*'}
     </label>
     <div className="grid grid-cols-2 gap-2">
       <select
         value={selectedMonth}
         onChange={(e) => {
           const newMonth = e.target.value;
           onChange(`${newMonth}-${selectedYear}`);
         }}
         className="block w-full pvs-select"
         required={required}
       >
         <option value="">Month</option>
         {months.map((month, index) => (
           <option key={month} value={String(index + 1).padStart(2, '0')}>
             {month}
           </option>
         ))}
       </select>

       <select
         value={selectedYear}
         onChange={(e) => {
           const newYear = e.target.value;
           onChange(`${selectedMonth}-${newYear}`);
         }}
         className="block w-full pvs-select"
         required={required}
       >
         <option value="">Year</option>
         {years.map(year => (
           <option key={year} value={year}>
             {year}
           </option>
         ))}
       </select>
     </div>
   </div>
 );
};

const getNamePlateStamp = (examinationType) => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = String(now.getFullYear()).slice(-2);
  const suffix = examinationType === 'thorough' ? 'XX' : 'X';
  return `${month} ${year} ${suffix}`;
};

const TankCertificationForm = ({ onBack }) => {
  const [formData, setFormData] = useState({

    certificateNumber: '',
    customerName: '',
    preparationDepot: 'PVS Ltd Halifax',
    telephone: '01422 201331',
    examinationType: 'inService',
    examinationDate: new Date().toISOString().split('T')[0],
    manufacturer: '',
    dateOfManufacture: '',
    namePlateStamped: getNamePlateStamp('inService'),
    liquidOffTakeDate: '',
    previousTestDate: '',
    vesselSerialNumber: '',
    waterCapacity: '',
    valveLayout: 'Normal',
    color: '',
    liquidOfftakePosition: '',
    designCode: '',
    designPressure: '',
    PRVADate: '',
    vapourValveDate: '',
    externalSurfacesExamined: false,
    fixedLiquidLevelChecked: false,
    vapourOffTakeValveChecked: false,
    allValvesChecked: false,
    nextTestYear: new Date().getFullYear(),
    reliefValveDetails: '',
    reliefValveType: '',
    pressureSettings: '',
    reliefValveDate: '',
    fillValveDate: '',
    designTemperature: '+40°C to -40°C',
    torqueWrenchUsed: '',
    controlReadings: {
      mainShell: '',
      endShell1: '',
      endShell2: ''
    },
    readings: Array(38).fill(''),
    examinerName: '',
    signatureDate: ''
   
  });
  
  const [additionalShells, setAdditionalShells] = useState([]);
  const [pressureWarning, setPressureWarning] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [serialModalOpen, setSerialModalOpen] = useState(false);
  const [serialModalMessage, setSerialModalMessage] = useState('');
  const [serialLookupState, setSerialLookupState] = useState({ loading: false, notFound: false, lastLookup: '' });
  const [missingVesselFields, setMissingVesselFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const MISSING_PLACEHOLDER = 'Unable to obtain — please enter';
  const closeSerialModal = useCallback(() => setSerialModalOpen(false), []);

  const handleSerialLookup = useCallback(async () => {
    const serial = String(formData.vesselSerialNumber || '').trim();
    if (!serial || serial === serialLookupState.lastLookup) return;

    setSerialLookupState({ loading: true, notFound: false, lastLookup: serial });
    try {
      const result = await lookupVesselBySerial(serial);
      if (!result.found) {
        setSerialLookupState({ loading: false, notFound: true, lastLookup: serial });
        setMissingVesselFields({});
        return;
      }

      const vessel = result.vessel || {};
      const missing = {};
      const hasManufacturer = !!vessel.vesselManufacturer;
      const hasOwner = !!vessel.owner;
      const hasDesignPressure = vessel.designPressure !== null && vessel.designPressure !== undefined && vessel.designPressure !== '';
      const hasVesselSize = vessel.vesselSize !== null && vessel.vesselSize !== undefined && vessel.vesselSize !== '';
      const hasDateOfManufacture = !!vessel.dateOfManufacture;

      if (!hasManufacturer) missing.manufacturer = true;
      if (!hasOwner) missing.customerName = true;
      if (!hasDesignPressure) missing.designPressure = true;
      if (!hasVesselSize) missing.waterCapacity = true;
      if (!hasDateOfManufacture) missing.dateOfManufacture = true;

      setFormData(prev => {
        const next = { ...prev };
        if (!prev.manufacturer && hasManufacturer) {
          next.manufacturer = vessel.vesselManufacturer;
        }
        if (!prev.customerName && hasOwner) {
          next.customerName = vessel.owner;
        }
        if ((prev.designPressure === '' || prev.designPressure === null || prev.designPressure === undefined)
            && hasDesignPressure) {
          next.designPressure = String(vessel.designPressure);
        }
        if ((prev.waterCapacity === '' || prev.waterCapacity === null || prev.waterCapacity === undefined)
            && hasVesselSize) {
          next.waterCapacity = String(vessel.vesselSize);
        }
        if (!prev.dateOfManufacture && hasDateOfManufacture) {
          const d = new Date(vessel.dateOfManufacture);
          if (!Number.isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            next.dateOfManufacture = `${yyyy}-${mm}`;
          }
        }
        return next;
      });
      setMissingVesselFields(missing);
      setSerialLookupState({ loading: false, notFound: false, scrapped: isVesselScrapped(vessel), lastLookup: serial });
    } catch (error) {
      console.warn('Vessel lookup failed:', error);
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
    }
  }, [formData.vesselSerialNumber, serialLookupState.lastLookup]);

  const addAdditionalShell = () => {
    setAdditionalShells(prev => [
      ...prev,
      { controlReading: '', readings: Array(19).fill('') }
    ]);
  };

  const removeAdditionalShell = (shellIndex) => {
    setAdditionalShells(prev => prev.filter((_, i) => i !== shellIndex));
  };

  const handleAdditionalShellControl = (shellIndex, value) => {
    setAdditionalShells(prev => prev.map((shell, i) =>
      i === shellIndex ? { ...shell, controlReading: value } : shell
    ));
  };

  const handleAdditionalShellReading = (shellIndex, readingIndex, value) => {
    setAdditionalShells(prev => prev.map((shell, i) => {
      if (i !== shellIndex) return shell;
      const newReadings = [...shell.readings];
      newReadings[readingIndex] = value;
      return { ...shell, readings: newReadings };
    }));
  };

  const isDifferentialTooHigh = (reading, control) => {
    if (!control || !reading) return false;
    const differential = Math.abs(parseFloat(reading) - parseFloat(control)) / parseFloat(control) * 100;
    return differential >= 10;
  };

  const validateReadings = useCallback(() => {
    const errors = [];
    
    if (formData.controlReadings.mainShell) {
      formData.readings.slice(0, 19).forEach((reading, index) => {
        if (reading) {
          const differential = Math.abs(parseFloat(reading) - parseFloat(formData.controlReadings.mainShell)) 
                             / parseFloat(formData.controlReadings.mainShell) * 100;
          if (differential >= 10) {
            errors.push(`Warning: Main Shell reading ${index + 1} exceeds the control reading by ${differential.toFixed(1)}%`);
          }
        }
      });
    }
  
    if (formData.controlReadings.endShell1) {
      formData.readings.slice(19, 29).forEach((reading, index) => {
        if (reading) {
          const differential = Math.abs(parseFloat(reading) - parseFloat(formData.controlReadings.endShell1)) 
                             / parseFloat(formData.controlReadings.endShell1) * 100;
          if (differential >= 10) {
            errors.push(`Warning: End Shell 1 reading ${index + 20} exceeds the control reading by ${differential.toFixed(1)}%`);
          }
        }
      });
    }
  
    if (formData.controlReadings.endShell2) {
      formData.readings.slice(29).forEach((reading, index) => {
        if (reading) {
          const differential = Math.abs(parseFloat(reading) - parseFloat(formData.controlReadings.endShell2)) 
                             / parseFloat(formData.controlReadings.endShell2) * 100;
          if (differential >= 10) {
            errors.push(`Warning: End Shell 2 reading ${index + 30} exceeds the control reading by ${differential.toFixed(1)}%`);
          }
        }
      });
    }
  
    additionalShells.forEach((shell, shellIndex) => {
      if (shell.controlReading) {
        shell.readings.forEach((reading, index) => {
          if (reading) {
            const differential = Math.abs(parseFloat(reading) - parseFloat(shell.controlReading))
                               / parseFloat(shell.controlReading) * 100;
            if (differential >= 10) {
              errors.push(`Warning: Additional Shell ${shellIndex + 1} reading ${index + 1} exceeds the control reading by ${differential.toFixed(1)}%`);
            }
          }
        });
      }
    });

    return errors;
  }, [formData.readings, formData.controlReadings, additionalShells]);

  useEffect(() => {
    validateReadings();
  }, [formData.readings, validateReadings]);

  useEffect(() => {
    const name = getCurrentUserDisplayName();
    if (!name) return;
    setFormData(prev => prev.examinerName ? prev : { ...prev, examinerName: name });
  }, []);
  
  const handleControlChange = (section, value) => {
    setFormData(prev => ({
      ...prev,
      controlReadings: {
        ...prev.controlReadings,
        [section]: value
      }
    }));
  };
  

    
    
  
  const handleInputChange = (field, value) => {
  
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

        if (field === 'examinationType') {
          newData.namePlateStamped = getNamePlateStamp(value);
        }
  
        if (field === 'designPressure' || field === 'pressureSettings') {
          const designPressure = parseFloat(field === 'designPressure' ? value : prev.designPressure);
          const reliefValvePressure = parseFloat(field === 'pressureSettings' ? value : prev.pressureSettings);
    
          if (!isNaN(designPressure) && !isNaN(reliefValvePressure)) {
            const roundedDesign = Math.round(designPressure * 100) / 100;
            const roundedRelief = Math.round(reliefValvePressure * 100) / 100;
    
            setPressureWarning(roundedRelief > roundedDesign);
          }
        }
  
  
      return newData;
  
  });
  };

  const handleReadingChange = (index, value) => {
    const newReadings = [...formData.readings];
    newReadings[index] = value;
    setFormData(prev => ({
      ...prev,
      readings: newReadings
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const validationErrors = validateReadings();

    if (parseFloat(formData.pressureSettings) > parseFloat(formData.designPressure)) {
      alert("Warning: Relief Valve Pressure is higher than vessel design pressure");
      return;
    }

    if (validationErrors.length > 0) {
      alert(validationErrors.join('\n'));
      return;
    }

    setIsSubmitting(true);

    const isInService = formData.examinationType === 'inService';

    try {
      if (isInService) {
        await assertVesselNotScrapped(formData.vesselSerialNumber, 'tankCertification');
      } else {
        await validateFormCompletion(formData.vesselSerialNumber, 'tankCertification', {
          examinationType: formData.examinationType
        });
      }
    } catch (error) {
      console.error('Serial validation failed:', error);
      setSerialModalMessage(error.message || 'Vessel Serial Number not found in Vessel Inventory.');
      setSerialModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const success = await sendCertificationEmail({ ...formData, additionalShells });
      if (!success) {
        alert('Failed to submit form');
        setIsSubmitting(false);
        return;
      }

      if (!isInService) {
        await markFormCompleted(
          formData.vesselSerialNumber,
          'tankCertification',
          formData.signatureDate || formData.examinationDate || new Date().toISOString().split('T')[0],
          { examinationType: formData.examinationType, completedBy: formData.examinerName }
        );
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(error.message || 'Failed to submit form');
      setIsSubmitting(false);
    }
  };

  const generateFutureYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, i) => currentYear + i).map(year => (
      <option key={year} value={year}>{year}</option>
    ));
  };
  
  const futureYearOptions = generateFutureYearOptions();

  return (
        
        <PVSPageLayout className="p-2 sm:p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="pvs-glass rounded-xl shadow-pvs-glow p-3 sm:p-6 border border-pvs-accent/30">
              {/* Home Button */}
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={onBack}
                  className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                  title="Return to Forms"
                >
                  <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
                </button>
              </div>
              
              {/* Header */}
              <div className="text-center mb-8">
                <div className="relative inline-block">
                  <span className="text-[#32CD32] text-6xl font-bold">PVS</span>
                  <div className="absolute -inset-4 border-4 border-pvs-accent rounded-full"></div>
                </div>
                <h1 className="text-[#32CD32] text-2xl font-bold mt-4 underline decoration-pvs-accent">
                  Pressure Vessel Services
                </h1>
                <h2 className="text-pvs-text-muted text-xl mt-4">
                  BULK TANK THOROUGH EXAMINATION CERTIFICATE
                </h2>
                <p className="text-pvs-text-dim">
                  EXAMINED IN ACCORDANCE WITH UKLPG CODE OF PRACTICE 1 PART 3 2022
                </p>
                <p className="text-xs text-pvs-text-dim mt-2">Ref: PVS-TCF-001 | Version: 1.0</p>
              </div>
             
   
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Form fields */}
            <div className="flex flex-col min-h-full">
        <div className="flex-grow space-y-6">
              {/* Certificate Number */}
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted">
                  Certificate Number
                </label>
                <input
                  type="text"
                  value={formData.certificateNumber}
                  onChange={(e) => handleInputChange('certificateNumber', e.target.value)}
                  className="mt-1 block w-full pvs-input"
                />
              </div>
              </div>

              {/* Vessel Serial Number */}
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted">
                  Vessel Serial Number *
                </label>
                <input
                  type="text"
                  required
                  value={formData.vesselSerialNumber}
                  onChange={(e) => {
                    handleInputChange('vesselSerialNumber', e.target.value);
                    if (serialLookupState.lastLookup) {
                      setSerialLookupState({ loading: false, notFound: false, lastLookup: '' });
                      setMissingVesselFields({});
                    }
                  }}
                  onBlur={handleSerialLookup}
                  className="mt-1 block w-full pvs-input"
                />
                {serialLookupState.loading && (
                  <p className="mt-1 text-xs text-pvs-text-dim">Looking up vessel…</p>
                )}
                {!serialLookupState.loading && serialLookupState.notFound && (
                  <p className="mt-1 text-xs text-red-400">
                    Serial number not found in Vessel Inventory.
                  </p>
                )}
                {!serialLookupState.loading && serialLookupState.scrapped && (
                  <p className="mt-1 text-xs text-red-400">
                    {getScrappedFormMessage('tankCertification')}
                  </p>
                )}
              </div>

              {/* Customer Name */}
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted">
                  Customer Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.customerName}
                  onChange={(e) => handleInputChange('customerName', e.target.value)}
                  placeholder={missingVesselFields.customerName ? MISSING_PLACEHOLDER : ''}
                  className="mt-1 block w-full pvs-input"
                />
              </div>

              {/* Form fields grid */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">

  
 

  
  {/* Preparation Depot */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Preparation Depot
    </label>
    <input
      type="text"
      readOnly
      value={formData.preparationDepot}
      className="mt-1 block w-full rounded-md border-pvs-border bg-pvs-card-light shadow-sm text-pvs-text-dim"
    />
  </div>

  {/* Telephone */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Telephone
    </label>
    <input
      type="text"
      readOnly
      value={formData.telephone}
      className="mt-1 block w-full rounded-md border-pvs-border bg-pvs-card-light shadow-sm text-pvs-text-dim"
    />
  </div>

  {/* Examination Type */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Examination Type *
    </label>
    <div className="mt-1 flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => handleInputChange('examinationType', 'inService')}
        className={`px-4 py-2 rounded-md flex-1 sm:flex-none ${
          formData.examinationType === 'inService'
            ? 'bg-pvs-accent text-pvs-bg'
            : 'bg-pvs-input-bg text-pvs-text-muted'
        }`}
      >
        In Service
      </button>
      <button
        type="button"
        onClick={() => handleInputChange('examinationType', 'thorough')}
        className={`px-4 py-2 rounded-md flex-1 sm:flex-none ${
          formData.examinationType === 'thorough'
            ? 'bg-pvs-accent text-pvs-bg'
            : 'bg-pvs-input-bg text-pvs-text-muted'
        }`}
      >
        Thorough
      </button>
    </div>
  </div>

  {/* Date of Examination */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Date of Examination *
    </label>
    <input
      type="date"
      required
      value={formData.examinationDate}
      onChange={(e) => handleInputChange('examinationDate', e.target.value)}
      className="mt-1 block w-full pvs-input"
    />
  </div>

  {/* Manufacturer */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Manufacturer
    </label>
    <input
      type="text"
      value={formData.manufacturer}
      onChange={(e) => handleInputChange('manufacturer', e.target.value)}
      placeholder={missingVesselFields.manufacturer ? MISSING_PLACEHOLDER : ''}
      className="mt-1 block w-full pvs-input"
    />
  </div>

  {/* Date of Manufacture */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Date of Manufacture
    </label>
    <input
      type="text"
      value={formData.dateOfManufacture}
      onChange={(e) => handleInputChange('dateOfManufacture', e.target.value)}
      placeholder={missingVesselFields.dateOfManufacture ? MISSING_PLACEHOLDER : 'e.g. 2020-05 or May 2020'}
      className="mt-1 block w-full pvs-input"
    />
  </div>

  {/* Name Plate Stamped */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Name Plate Stamped *
    </label>
    <input
      type="text"
      required
      value={formData.namePlateStamped}
      onChange={(e) => handleInputChange('namePlateStamped', e.target.value)}
      className="mt-1 block w-full pvs-input"
    />
  </div>

 

  {/* Previous Test Date */}
  <div>
  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
    Previous Test Date *
  </label>
  <input
    type="text"
    required
    value={formData.previousTestDate}
    onChange={(e) => handleInputChange('previousTestDate', e.target.value)}
    className="mt-1 block w-full pvs-input"
  />
</div>

  {/* Water Capacity */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Water Capacity *
    </label>
    <input
      type="number"
      required
      min="0"
      value={formData.waterCapacity}
      onChange={(e) => handleInputChange('waterCapacity', e.target.value)}
      placeholder={missingVesselFields.waterCapacity ? MISSING_PLACEHOLDER : ''}
      className="mt-1 block w-full pvs-input"
    />
  </div>

  {/* Valve Layout */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Valve Layout
    </label>
    <div className="mt-1 flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => handleInputChange('valveLayout', 'Normal')}
        className={`px-4 py-2 rounded-md flex-1 sm:flex-none ${formData.valveLayout === 'Normal' ? 'bg-pvs-accent text-pvs-bg' : 'bg-pvs-input-bg text-pvs-text-muted'}`}
      >
        Normal
      </button>
      <button
        type="button"
        onClick={() => handleInputChange('valveLayout', 'Combi')}
        className={`px-4 py-2 rounded-md flex-1 sm:flex-none ${formData.valveLayout === 'Combi' ? 'bg-pvs-accent text-pvs-bg' : 'bg-pvs-input-bg text-pvs-text-muted'}`}
      >
        Combi
      </button>
    </div>
  </div>


{/* Color */}
<div>
  <label className="block text-sm font-medium text-pvs-text-muted">
    Color *
  </label>
  <div className="space-y-2">
    <select
      required
      value={formData.color === 'Other' ? 'Other' : formData.color}
      onChange={(e) => {
        if (e.target.value === 'Other') {
          handleInputChange('color', 'Other');
        } else {
          handleInputChange('color', e.target.value);
        }
      }}
      className="mt-1 block w-full pvs-select"
    >
      <option value="">Select a color</option>
      <option value="Green 12B21">Green 12B21</option>
      <option value="Light Green 12B17">Light Green 12B17</option>
      <option value="White">White</option>
      <option value="Other">Other (Please Specify)</option>
    </select>

    {formData.color === 'Other' && (
      <input
        type="text"
        placeholder="Please specify color"
        value={formData.colorOther || ''}
        onChange={(e) => {
          handleInputChange('colorOther', e.target.value);
          handleInputChange('color', `Other: ${e.target.value}`);
        }}
        className="mt-2 block w-full pvs-input"
      />
    )}
  </div>
</div>

 {/* Liquid Offtake Position */}
 <div>
   <label className="block text-sm font-medium text-pvs-text-muted">
     Liquid Offtake Position
   </label>
   <select
     value={formData.liquidOfftakePosition}
     onChange={(e) => handleInputChange('liquidOfftakePosition', e.target.value)}
     className="mt-1 block w-full pvs-select"
   >
     <option value="">Select position</option>
     <option value="Top">Top</option>
     <option value="Bottom">Bottom</option>
     <option value="Both">Both</option>
   </select>
 </div>

 {/* Design Code */}
 <div>
   <label className="block text-sm font-medium text-pvs-text-muted">
     Design Code *
   </label>
   <input
     type="text"
     required
     value={formData.designCode}
     onChange={(e) => handleInputChange('designCode', e.target.value)}
     className="mt-1 block w-full pvs-input"
   />
 </div>

 {/* Design Pressure */}
<div>
  <label className="block text-sm font-medium text-pvs-text-muted">
    Design Pressure *
  </label>
  <input
    type="number"
    required
    min="0"
    step="0.01"
    value={formData.designPressure}
    onChange={(e) => handleInputChange('designPressure', e.target.value)}
    placeholder={missingVesselFields.designPressure ? MISSING_PLACEHOLDER : ''}
    className={`mt-1 block w-full rounded-md shadow-sm focus:ring-pvs-accent/50 ${
      pressureWarning 
        ? 'border-red-500 focus:border-red-500 bg-pvs-input-bg text-white' 
        : 'pvs-input'
    }`}
  />
  {pressureWarning && (
    <p className="mt-1 text-sm text-red-400">
      Warning: Relief Valve Pressure is higher than vessel design pressure
    </p>
  )}
</div>

 {/* PRVA Date */}
 <MonthYearPicker
  label="PRVA Date"
  value={formData.PRVADate}
  onChange={(value) => handleInputChange('PRVADate', value)}
  required={true}
/>


 {/* Vapour Valve Date */}
 <MonthYearPicker
  label="Vapour Valve Date"
  value={formData.vapourValveDate}
  onChange={(value) => handleInputChange('vapourValveDate', value)}
  required={true}
/>

 {/* Liquid Off Take Date */}
 <MonthYearPicker
 label= "Liquid Offtake Date"
  value={formData.liquidOffTakeDate}
  onChange={(value) => handleInputChange('liquidOffTakeDate', value)}
  required={true}
/>

 

 {/* Next Test Year */}
 <div>
  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
    This Vessel Will Require a Test in: *
  </label>
  <select
    required
    value={formData.nextTestYear}
    onChange={(e) => handleInputChange('nextTestYear', e.target.value)}
    className="mt-1 block w-full pvs-select"
  >
    {futureYearOptions}
  </select>
</div>

{/* Relief Valve Details */}
<div>
   <label className="block text-sm font-medium text-pvs-text-muted">
     Relief Valve Details *
   </label>
   <input
     type="text"
     required
     value={formData.reliefValveDetails}
     onChange={(e) => handleInputChange('reliefValveDetails', e.target.value)}
     className="mt-1 block w-full pvs-input"
   />
 </div>

 {/* Relief Valve Type */}
<div>
  <label className="block text-sm font-medium text-pvs-text-muted">
    Relief Valve Type *
  </label>
  <div className="space-y-2">
    <select
      required
      value={formData.reliefValveType === 'Other' ? 'Other' : formData.reliefValveType}
      onChange={(e) => {
        if (e.target.value === 'Other') {
          handleInputChange('reliefValveType', 'Other');
        } else {
          handleInputChange('reliefValveType', e.target.value);
        }
      }}
      className="mt-1 block w-full pvs-select"
    >
      <option value="">Select valve type</option>
      <option value="SRG">SRG</option>
      <option value="Omeca">Omeca</option>
      <option value="Rego">Rego</option>
      <option value="Other">Other (Please Specify)</option>
    </select>

    {formData.reliefValveType === 'Other' && (
      <input
        type="text"
        placeholder="Please specify valve type"
        value={formData.reliefValveTypeOther || ''}
        onChange={(e) => {
          handleInputChange('reliefValveTypeOther', e.target.value);
          handleInputChange('reliefValveType', `Other: ${e.target.value}`);
        }}
        className="mt-2 block w-full pvs-input"
      />
    )}
  </div>
</div>
 {/* Relief Valve Pressure */}
<div>
  <label className="block text-sm font-medium text-pvs-text-muted">
    Relief Valve Pressure (BAR) *
  </label>
  <input
    type="number"
    required
    min="0"
    step="0.01"
    value={formData.pressureSettings}
    onChange={(e) => handleInputChange('pressureSettings', e.target.value)}
    className={`mt-1 block w-full rounded-md shadow-sm focus:ring-pvs-accent/50 ${
      pressureWarning 
        ? 'border-red-500 focus:border-red-500 bg-pvs-input-bg text-white' 
        : 'pvs-input'
    }`}
  />
  {pressureWarning && (
    <p className="mt-1 text-sm text-red-400">
      Warning: Relief Valve Pressure is higher than vessel design pressure
    </p>
  )}
</div>

 {/* Relief Valve Date */}
 <MonthYearPicker
  label="Relief Valve Date"
  value={formData.reliefValveDate}
  onChange={(value) => handleInputChange('reliefValveDate', value)}
  required={true}
/>

 {/* Fill Valve Date */}
 <MonthYearPicker
  label="Fill Valve Date"
  value={formData.fillValveDate}
  onChange={(value) => handleInputChange('fillValveDate', value)}
  required={true}
/>

 {/* Design Temperature */}
 <div>
   <label className="block text-sm font-medium text-pvs-text-muted">
     Design Temperature *
   </label>
   <input
     type="text"
     required
     value={formData.designTemperature}
     onChange={(e) => handleInputChange('designTemperature', e.target.value)}
     className="mt-1 block w-full pvs-input"
   />
 </div>

 {/* Torque Wrench Used */}
 <div>
   <label className="block text-sm font-medium text-pvs-text-muted">
     Torque Wrench Used
   </label>
   <select
     value={formData.torqueWrenchUsed}
     onChange={(e) => handleInputChange('torqueWrenchUsed', e.target.value)}
     className="mt-1 block w-full pvs-select"
   >
     <option value="">Select torque wrench</option>
     <option value="Torque 1">Torque 1</option>
     <option value="Torque 2">Torque 2</option>
    <option value="Torque 3">Torque 3</option>
   </select>
 </div>

{/* Checkboxes Section */}
<div className="col-span-1 md:col-span-2 space-y-4">
   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.externalSurfacesExamined}
       required
       onChange={(e) => handleInputChange('externalSurfacesExamined', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
       External Surfaces and Welds Examined*
     </label>
   </div>

   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.fixedLiquidLevelChecked}
       required
       onChange={(e) => handleInputChange('fixedLiquidLevelChecked', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
       Fixed Liquid Level Checked*
     </label>
   </div>

   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.vapourOffTakeValveChecked}
       required
       onChange={(e) => handleInputChange('vapourOffTakeValveChecked', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
       Vapour Off Take Valve Operational Check*
     </label>
   </div>

   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.allValvesChecked}
       required
       onChange={(e) => handleInputChange('allValvesChecked', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
       All Valves and Fittings Checked or Replaced*
     </label>
   </div>
 </div>

 <div className="col-span-1 md:col-span-2 space-y-4">
   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.paintworkExamined}
       required
       onChange={(e) => handleInputChange('paintworkExamined', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
       Paint Work Thickness Checked*
     </label>
   </div>

   <div className="col-span-1 md:col-span-2 space-y-4">
   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.paintworkVisual}
       required
       onChange={(e) => handleInputChange('paintworkVisual', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
       Paint Work Visually Inspected for Imperfections*
     </label>
   </div>
   </div>
   

   <div className="col-span-1 md:col-span-2 space-y-4">
   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.contentsOp}
       required
       onChange={(e) => handleInputChange('contentsOp', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
     Guage Operational Check Before Installation*
     </label>
   </div>
   </div>

   <div className="col-span-1 md:col-span-2 space-y-4">
   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.idSticker}
       required
       onChange={(e) => handleInputChange('idSticker', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
       Identity Tag Or Sticker Fitted*
     </label>
   </div>
   </div>


   <div className="col-span-1 md:col-span-2 space-y-4">
   <div className="flex items-center">
     <input
       type="checkbox"
       checked={formData.pressureTest}
       required
       onChange={(e) => handleInputChange('pressureTest', e.target.checked)}
       className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
     />
     <label className="ml-2 block text-sm text-pvs-text-muted">
       Vessel Has Been Correctly Pressure Tested*
     </label>
   </div>
   </div>



{/* NDT Readings Section */}
<div className="col-span-1 md:col-span-2">
  <h3 className="text-lg font-bold underline text-white mb-4">
    Ultrasonic NDT Readings
  </h3>
  <p className="text-pvs-text-dim mb-6">
    All results in millimeters recorded to the nearest 0.1 mm. Results 1,3,4,8,9,13,14,19,20,21,22,23,29,30,31 & 32 should be taken 25 mm from the adjacent weld.  
    If there is evidence of corrosion additional readings should be taken in the corroded area. If a reading is considerably different from other readings retake the reading.  
    Additional readings should be numbered from 38 and their positions marked on the above diagram.
  </p>

  {/* Main Shell Section */}
  <h4 className="font-bold underline mb-3 text-white">Main Shell</h4>
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
    {/* Control Reading for Main Shell */}
    <div className="col-span-2 sm:col-span-4 mb-4">
      <label className="block text-sm font-bold text-pvs-text-muted">
        Control Reading
      </label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={formData.controlReadings.mainShell}
        onChange={(e) => handleControlChange('mainShell', e.target.value)}
        className="mt-1 block w-full pvs-input"
      />
    </div>
    {/* Main Shell Readings */}
    {formData.readings.slice(0, 19).map((reading, index) => (
      <div key={index}>
        <label className="block text-sm font-medium text-pvs-text-muted">
          Reading {index + 1}
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={reading}
          onChange={(e) => handleReadingChange(index, e.target.value)}
          className={`mt-1 block w-full rounded-md shadow-sm focus:ring-pvs-accent/50 ${
            isDifferentialTooHigh(reading, formData.controlReadings.mainShell)
              ? 'border-red-500 focus:border-red-500 bg-pvs-input-bg text-white'
              : 'pvs-input'
          }`}
        />
      </div>
    ))}
  </div>

  {/* Additional Shell Sections */}
  {additionalShells.map((shell, shellIndex) => (
    <div key={`additional-shell-${shellIndex}`} className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold underline text-white">Additional Shell {shellIndex + 1}</h4>
        <button
          type="button"
          onClick={() => removeAdditionalShell(shellIndex)}
          className="px-3 py-1 text-sm text-red-400 border border-red-500/40 rounded-md hover:bg-red-500/10 transition-colors"
        >
          Remove
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="col-span-2 sm:col-span-4 mb-4">
          <label className="block text-sm font-bold text-pvs-text-muted">
            Control Reading
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={shell.controlReading}
            onChange={(e) => handleAdditionalShellControl(shellIndex, e.target.value)}
            className="mt-1 block w-full pvs-input"
          />
        </div>
        {shell.readings.map((reading, readingIndex) => (
          <div key={readingIndex}>
            <label className="block text-sm font-medium text-pvs-text-muted">
              Reading {readingIndex + 1}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={reading}
              onChange={(e) => handleAdditionalShellReading(shellIndex, readingIndex, e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-pvs-accent/50 ${
                isDifferentialTooHigh(reading, shell.controlReading)
                  ? 'border-red-500 focus:border-red-500 bg-pvs-input-bg text-white'
                  : 'pvs-input'
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  ))}

  {/* Add Additional Shell Button */}
  <div className="mb-6">
    <button
      type="button"
      onClick={addAdditionalShell}
      className="px-4 py-2 border border-pvs-accent/40 rounded-md text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 transition-colors"
    >
      + Add Additional Shell
    </button>
  </div>

  {/* End Shell 1 Section */}
  <h4 className="font-bold underline mb-3 text-white">End Shell 1</h4>
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
    {/* Control Reading for End Shell 1 */}
    <div className="col-span-2 sm:col-span-4 mb-4">
      <label className="block text-sm font-bold text-pvs-text-muted">
        Control Reading
      </label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={formData.controlReadings.endShell1}
        onChange={(e) => handleControlChange('endShell1', e.target.value)}
        className="mt-1 block w-full pvs-input"
      />
    </div>
    {/* End Shell 1 Readings */}
    {formData.readings.slice(19, 29).map((reading, index) => (
      <div key={index}>
        <label className="block text-sm font-medium text-pvs-text-muted">
          Reading {index + 20}
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={reading}
          onChange={(e) => handleReadingChange(index + 19, e.target.value)}
          className={`mt-1 block w-full rounded-md shadow-sm focus:ring-pvs-accent/50 ${
            isDifferentialTooHigh(reading, formData.controlReadings.endShell1)
              ? 'border-red-500 focus:border-red-500 bg-pvs-input-bg text-white'
              : 'pvs-input'
          }`}
        />
      </div>
    ))}
  </div>

  {/* End Shell 2 Section */}
  <h4 className="font-bold underline mb-3 text-white">End Shell 2</h4>
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
    {/* Control Reading for End Shell 2 */}
    <div className="col-span-2 sm:col-span-4 mb-4">
      <label className="block text-sm font-bold text-pvs-text-muted">
        Control Reading
      </label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={formData.controlReadings.endShell2}
        onChange={(e) => handleControlChange('endShell2', e.target.value)}
        className="mt-1 block w-full pvs-input"
      />
    </div>
    {/* End Shell 2 Readings */}
    {formData.readings.slice(29).map((reading, index) => (
      <div key={index}>
        <label className="block text-sm font-medium text-pvs-text-muted">
          Reading {index + 30}
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={reading}
          onChange={(e) => handleReadingChange(index + 29, e.target.value)}
          className={`mt-1 block w-full rounded-md shadow-sm focus:ring-pvs-accent/50 ${
            isDifferentialTooHigh(reading, formData.controlReadings.endShell2)
              ? 'border-red-500 focus:border-red-500 bg-pvs-input-bg text-white'
              : 'pvs-input'
          }`}
        />
      </div>
      
    ))}
  </div>
</div>

{/* Examiner Information */}
<div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-6">
  {/* Examiner Name */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Examiner Name *
    </label>
    <input
      type="text"
      required
      value={formData.examinerName}
      onChange={(e) => handleInputChange('examinerName', e.target.value)}
      className="mt-1 block w-full pvs-input"
    />
  </div>

  {/* Examiner Date */}
  <div>
    <label className="block text-sm font-medium text-pvs-text-muted">
      Date *
    </label>
    <input
      type="date"
      required
      value={formData.signatureDate}
      onChange={(e) => handleInputChange('signatureDate', e.target.value)}
      className="mt-1 block w-full pvs-input"
    />
  </div>
</div>

            {/* Form Actions */}
  
<div className="mt-8 border-t border-pvs-border pt-8">
  <div className="flex flex-col sm:flex-row justify-end gap-3">
    <button
      type="button"
      onClick={onBack}
      className="px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50"
    >
      Back to Forms
    </button>
    <button
      type="submit"
      disabled={isSubmitting}
      aria-busy={isSubmitting}
      className="px-6 py-2 bg-pvs-accent text-pvs-bg rounded-md shadow-sm text-sm font-semibold hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {isSubmitting ? 'Submitting…' : 'Submit Certification'}
    </button>
  </div>
  </div>
</div>
</div>
</div>
</form>
      </div>
    </div>
  <SerialValidationModal isOpen={serialModalOpen} message={serialModalMessage} onClose={closeSerialModal} />
  <FormSuccessModal isOpen={showSuccessModal} formName="Tank Certification" onClose={onBack} />
  </PVSPageLayout>
  );
};

export default TankCertificationForm;
